import { Briefcase, Code, GraduationCap, Palette } from 'lucide-react';

interface ExampleScenario {
  icon: any;
  title: string;
  description: string;
  income: string;
  expenses: string;
  net: string;
  color: string;
}

const examples: ExampleScenario[] = [
  {
    icon: Code,
    title: "Desarrollador freelance",
    description: "Desarrollo web con clientes nacionales y algunos europeos",
    income: "45.000€",
    expenses: "8.000€",
    net: "~26.000€",
    color: "#2563eb"
  },
  {
    icon: Briefcase,
    title: "Consultor",
    description: "Consultoría de gestión empresarial con retenciones del 15%",
    income: "60.000€",
    expenses: "12.000€",
    net: "~33.000€",
    color: "#16a34a"
  },
  {
    icon: Palette,
    title: "Diseñador gráfico",
    description: "Servicios creativos con gastos en software y equipo",
    income: "30.000€",
    expenses: "6.000€",
    net: "~18.000€",
    color: "#9333ea"
  },
  {
    icon: GraduationCap,
    title: "Formador",
    description: "Formación online con actividad exenta de IVA",
    income: "35.000€",
    expenses: "5.000€",
    net: "~21.500€",
    color: "#ea580c"
  }
];

export default function ExamplesSection() {
  return (
    <div
      style={{
        background: 'linear-gradient(180deg, #f8fafc 0%, white 100%)',
        padding: '64px 24px',
        borderTop: '1px solid #e5e7eb',
        marginTop: '64px'
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h2
            style={{
              fontSize: '32px',
              fontWeight: 700,
              color: '#1e293b',
              margin: '0 0 12px',
              letterSpacing: '-0.02em'
            }}
          >
            Casos de uso típicos
          </h2>
          <p
            style={{
              fontSize: '16px',
              color: '#64748b',
              margin: 0,
              lineHeight: '1.6'
            }}
          >
            Ejemplos orientativos de diferentes perfiles de autónomos
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '24px'
          }}
        >
          {examples.map((example, idx) => (
            <ExampleCard key={idx} {...example} />
          ))}
        </div>

        <div
          style={{
            marginTop: '48px',
            padding: '24px',
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            borderRadius: '12px',
            border: '1px solid #fbbf24',
            textAlign: 'center'
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: '14px',
              color: '#78350f',
              lineHeight: '1.6'
            }}
          >
            <strong>Nota importante:</strong> Estos son ejemplos simplificados para ilustrar diferentes situaciones.
            Tu caso real puede variar según región, situación personal, gastos específicos y otros factores.
            Usa la calculadora para obtener un cálculo personalizado.
          </p>
        </div>
      </div>
    </div>
  );
}

function ExampleCard({ icon: Icon, title, description, income, expenses, net, color }: ExampleScenario) {
  return (
    <div
      style={{
        background: 'white',
        borderRadius: '16px',
        border: '1px solid #e5e7eb',
        padding: '24px',
        transition: 'all 0.3s ease',
        cursor: 'default'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 12px 32px rgba(0, 0, 0, 0.1)';
        e.currentTarget.style.transform = 'translateY(-4px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '16px'
        }}
      >
        <Icon size={24} color={color} />
      </div>

      <h3
        style={{
          margin: '0 0 8px',
          fontSize: '18px',
          fontWeight: 700,
          color: '#1e293b'
        }}
      >
        {title}
      </h3>

      <p
        style={{
          margin: '0 0 20px',
          fontSize: '13px',
          color: '#64748b',
          lineHeight: '1.5',
          minHeight: '40px'
        }}
      >
        {description}
      </p>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          paddingTop: '16px',
          borderTop: '1px solid #f1f5f9'
        }}
      >
        <DataRow label="Ingresos anuales" value={income} />
        <DataRow label="Gastos deducibles" value={expenses} />
        <DataRow label="Neto estimado" value={net} highlight />
      </div>
    </div>
  );
}

function DataRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '13px'
      }}
    >
      <span style={{ color: '#64748b' }}>{label}</span>
      <span
        style={{
          fontWeight: highlight ? 700 : 600,
          color: highlight ? '#1e293b' : '#475569'
        }}
      >
        {value}
      </span>
    </div>
  );
}
